/**
 * BuchhaltungKonten — SKR04 Kontenplan tarayıcısı.
 * Sınıf filtresi + arama + yıl bakiyeleri. Salt okunur (kontenplan
 * migrasyonla seed edilir; düzenleme sonraki fazda).
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import PageShell from '@/components/ui/PageShell';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ListTree, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { listAccounts, fetchAccountTotals, accountBalance, fmtEUR } from '@/lib/buchhaltungApi';

const year = new Date().getFullYear();
const RANGE = { dateFrom: `${year}-01-01`, dateTo: `${year}-12-31` };

const CLASS_LABELS = {
  0: 'Anlagevermögen', 1: 'Umlaufvermögen', 2: 'Eigenkapital', 3: 'Fremdkapital',
  4: 'Erlöse', 5: 'Wareneinsatz', 6: 'Aufwendungen', 7: 'Finanz/Steuern', 9: 'Technisch',
};

export default function BuchhaltungKonten() {
  const [searchTerm, setSearchTerm] = useState('');
  const [classFilter, setClassFilter] = useState(null);

  const { data: accounts = [], isLoading } = useQuery({ queryKey: ['fibu-accounts'], queryFn: () => listAccounts() });
  const { data: totals } = useQuery({ queryKey: ['fibu-totals', RANGE], queryFn: () => fetchAccountTotals(RANGE) });

  const filtered = useMemo(() => {
    const needle = searchTerm.toLowerCase();
    return accounts.filter((a) =>
      (classFilter === null || a.account_class === classFilter) &&
      (a.account_no.includes(needle) || a.name.toLowerCase().includes(needle))
    );
  }, [accounts, searchTerm, classFilter]);

  const classes = [...new Set(accounts.map((a) => a.account_class))].sort();

  return (
    <PageShell
      head={(
        <div className="flex items-center gap-2">
          <PageHeader icon={<ListTree className="w-5 h-5" />} title="Kontenplan" />
          <Badge variant="outline" className="text-[10px]">SKR04 · {accounts.length} Konten</Badge>
        </div>
      )}
    >

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Kontonummer oder Name…"
          className="pl-10 h-11 bg-white"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Sınıf filtresi — yatay kaydırılabilir çipler */}
      <div className="flex gap-1.5 overflow-x-auto -mx-4 px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <button
          onClick={() => setClassFilter(null)}
          className={cn(
            'flex-shrink-0 min-h-[36px] px-3 rounded-full text-xs font-bold border transition-colors cursor-pointer',
            classFilter === null ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
          )}
        >
          Alle
        </button>
        {classes.map((c) => (
          <button
            key={c}
            onClick={() => setClassFilter(classFilter === c ? null : c)}
            className={cn(
              'flex-shrink-0 min-h-[36px] px-3 rounded-full text-xs font-bold border transition-colors cursor-pointer whitespace-nowrap',
              classFilter === c ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
            )}
          >
            {c} · {CLASS_LABELS[c] || c}
          </button>
        ))}
      </div>

      <Card className="divide-y divide-slate-100 overflow-hidden">
        {isLoading && <p className="p-8 text-center text-sm text-slate-400">Lade Kontenplan…</p>}
        {filtered.map((a) => {
          const bal = totals ? accountBalance(a, totals) : 0;
          return (
            <div key={a.account_no} className="flex items-center justify-between gap-3 px-3 py-2.5">
              <div className="min-w-0 flex items-center gap-3">
                <span className="font-mono text-xs font-bold text-slate-500 w-10 flex-shrink-0">{a.account_no}</span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-800 truncate">{a.name}</p>
                  <p className="text-[10px] text-slate-400 truncate">
                    {a.statement_type === 'guv' ? a.guv_position : a.balance_position || a.reporting_group}
                  </p>
                </div>
              </div>
              <span className={cn(
                'font-mono text-xs font-bold flex-shrink-0',
                bal === 0 ? 'text-slate-300' : bal > 0 ? 'text-slate-800' : 'text-red-600'
              )}>
                {bal === 0 ? '—' : fmtEUR(bal)}
              </span>
            </div>
          );
        })}
        {!isLoading && filtered.length === 0 && (
          <p className="p-8 text-center text-sm text-slate-400">Kein Konto gefunden.</p>
        )}
      </Card>
    </PageShell>
  );
}
